import {
  EventByIdQuery,
  EventManyAppQuery,
  useEventManyAppQuery,
} from "gql/generated";
import { ChangeEvent, useRef, useState } from "react";
import { useLoggedInAs } from "shared/hooks";
import { eventViewSections } from "./EventAppView.config";
import { IAppViewItem } from "shared/components";
import { getFilteredEvents } from "./getFilteredEvents";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { MAX_DATE } from "shared/utils";
import { ModalMode } from "./types/ModalMode";
export const useEvents = () => {
  // App view sections and selected app item
  const [appViewSections, setAppViewSections] = useState(eventViewSections);
  const [selectedAppItem, setSelectedAppItem] = useState<IAppViewItem>(
    appViewSections[0].items[0],
  );

  const { t } = useTranslation("events", { keyPrefix: "APP" });
  const loggedInAs = useLoggedInAs();
  const organizationId = loggedInAs?.entityId;

  // All events and filtered events
  const allEvents = useRef<EventManyAppQuery["eventMany"]>([]);
  const [filteredEvents, setFilteredEvents] = useState<
    EventManyAppQuery["eventMany"]
  >([]);

  /**
   * Update filtered events and app view sections
   */
  const updateFilteredEventsAndAppViewSections = () => {
    // Sort allEvents by last updated date
    allEvents.current = _.orderBy(
      allEvents.current,
      ["updatedDateISO"],
      ["desc"],
    );

    setFilteredEvents([
      ...getFilteredEvents(
        allEvents.current,
        selectedAppItem.key,
        organizationId,
        searchValue,
      ),
    ]);
    setAppViewSections((prev) =>
      prev.map((section) => ({
        items: section.items.map((item) => ({
          ...item,
          text: t(`HEADER.VIEWS.${item.key}.TITLE`),
          count:
            getFilteredEvents(
              allEvents.current,
              item.key,
              organizationId,
              searchValue,
            ).length ?? 0,
          isSelected: selectedAppItem?.key === item.key,
        })),
      })),
    );
  };

  /**
   * Fetch events
   */
  const { loading, error } = useEventManyAppQuery({
    variables: {
      organizationId,
      startDate: "2025-01-01",
      endDate: MAX_DATE,
      skipRecurrences: true,
    },
    onCompleted: (data) => {
      const events = data.eventMany ?? [];
      allEvents.current = events;
      updateFilteredEventsAndAppViewSections();
    },
    // This will ensure that our `onCompleted` re-runs while a refetch is happening
    notifyOnNetworkStatusChange: true,
  });

  /**
   * Update or add an event
   * - This allows us to skip refetching the events array when an event is modified in the modal
   * @param event
   */
  const updateOrAddEvent = (event: EventByIdQuery["eventById"]) => {
    if (!event) return;

    const events = [...allEvents.current];
    const existingEventIndex = allEvents.current.findIndex(
      (e) => e._id === event._id,
    );
    if (existingEventIndex !== -1) {
      events[existingEventIndex] = event as EventManyAppQuery["eventMany"][0];
    } else {
      events.push(event as EventManyAppQuery["eventMany"][0]);
    }
    allEvents.current = events;
    updateFilteredEventsAndAppViewSections();
  };

  /**
   * Remove an event from the events array
   * @param eventId
   */
  const removeEvent = (eventId: string) => {
    if (!eventId) return;

    const events = [...allEvents.current];
    const existingEventIndex = allEvents.current.findIndex(
      (e) => e._id === eventId,
    );
    if (existingEventIndex !== -1) {
      events.splice(existingEventIndex, 1);
    }
    allEvents.current = events;
    updateFilteredEventsAndAppViewSections();
  };

  /**
   * Handle app view change
   * - Filter events based on app view item
   * @param selectedItem
   */
  const handleAppViewChange = (selectedItem: IAppViewItem) => {
    const events = getFilteredEvents(
      allEvents.current ?? [],
      selectedItem.key,
      organizationId,
      searchValue,
    );
    setFilteredEvents([...events]);
    setSelectedAppItem(selectedItem);
    setAppViewSections((prev) =>
      prev.map((section) => ({
        items: section.items.map((item) => ({
          ...item,
          isSelected: selectedItem.key === item.key,
        })),
      })),
    );
  };

  const [searchValue, setSearchValue] = useState("");
  /**
   * Handle search value change
   * - Filter events based on search value
   * @param e
   */
  const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchVal = e.target.value;
    setSearchValue(searchVal);

    const events = getFilteredEvents(
      allEvents.current ?? [],
      selectedAppItem.key,
      organizationId,
      searchVal,
    );

    setFilteredEvents(events);
  };

  const handleGetEventCsvData = async (): Promise<Record<string, string>[]> => {
    return [];
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("create");
  const [eventId, setEventId] = useState<string | undefined>(undefined);

  const handleModalOpen = (mode: ModalMode, eventId?: string) => {
    setIsModalOpen(true);
    setModalMode(mode);
    setEventId(eventId);
  };

  return {
    events: filteredEvents,
    loading,
    error,
    appViewSections,
    isLoadingOperatorCsvData: false,
    searchValue,
    selectedAppItem,
    isModalOpen,
    modalMode,
    eventId,
    handleModalOpen,
    onAppViewChange: handleAppViewChange,
    onGetEventCsvData: handleGetEventCsvData,
    onSearchValueChange: handleSearchValueChange,
    setIsModalOpen,
    updateOrAddEvent,
    removeEvent,
  };
};
